/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Remembers the checkout this tab just walked away to, so that coming back
 * without having paid can say so to the SERVER and not only to the screen.
 *
 * WHY IT IS NEEDED. Pressing Pay reserves the places and then leaves the site.
 * Press the browser's back button on the gateway's page and the browser
 * restores this tab from cache: the runner is told the payment did not happen
 * — that part already worked — but nothing tells the server, which goes on
 * holding places for a payment that will never arrive. Somebody else is then
 * refused places that are, in truth, free.
 *
 * WHAT IT DOES. It records one id — the payment the gateway was opened for —
 * and nothing else. On return, the page asks the status endpoint about that id,
 * and that endpoint already does the deciding: it asks the gateway whether an
 * attempt is in flight, and gives the places back only when the answer is that
 * nobody ever tried to pay. So this file carries no judgement of its own; it
 * carries the id that makes the question askable.
 *
 * WHAT IT IS NOT. It is not permission, and it is not a cancellation. A runner
 * who clears it, or returns in a different tab, loses nothing but the instant
 * release — the places still come back when the gateway order lapses, and a
 * blocked buyer still prompts the same question. Nothing here is consulted to
 * decide whether a write is allowed.
 *
 * sessionStorage, not localStorage, and that is deliberate: this is only ever
 * true of the tab that walked away. A second tab has its own handover or none,
 * and must not act on this one's.
 */

const KEY = 'athlohive:checkout-in-flight';

/** Records the payment this tab has just handed over to the gateway. */
export function rememberInFlight(paymentId) {
  if (paymentId === undefined || paymentId === null || paymentId === '') return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({
      paymentId: String(paymentId),
      at: Date.now(),
    }));
  } catch (e) {
    // Deliberately silent. A private window or blocked site data costs the
    // instant release, never a place and never a payment.
  }
}

/**
 * The handover this tab is in the middle of, or null.
 *
 * Anything that is not the shape written here is treated as absent rather than
 * trusted: the value is editable by whoever owns the browser, and the only
 * thing it is used for is which id to ask the server about — a question the
 * server answers against its own records and the buyer's own session.
 */
export function recallInFlight() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.paymentId) return null;
    return { paymentId: String(parsed.paymentId), at: Number(parsed.at) || 0 };
  } catch (e) {
    return null;
  }
}

/** Forgets it — on return, whatever the answer turned out to be. */
export function forgetInFlight() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // Silent, as above.
  }
}
